import React, { Fragment, useEffect, useMemo } from 'react'
import tw from 'twin.macro'
import { InputAdornment, Typography } from '@mui/material'
import { FormikTextField } from '../shared/form/formik/FormikTextField'
import { Driver } from 'src/interfaces/driver'
import { useFormikContext } from 'formik'
import { getCardBrand } from './BankPaymentForm.validator'
import { CardBrand } from './BankPaymentForm.types'
import { GSIcon } from 'src/components/shared/GSIcon'
import { InfoContainer } from '../shared/InfoContainer'

interface DebitCardContentProps {
  handleChange: (e: React.ChangeEvent<any>) => void
  driver: Driver | null
  businessEntityStepCompleted: boolean
}

const CARD_BRAND_ICON_MAP: Partial<Record<CardBrand, 'gs_visa' | 'gs_mastercard'>> = {
  visa: 'gs_visa',
  mastercard: 'gs_mastercard',
}

const DATE_MASK = [/\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]

const ZIP_CODE_MASK = [/\d/, /\d/, /\d/, /\d/, /\d/]

const CARD_NUMBER_MASK = [
  /\d/,
  /\d/,
  /\d/,
  /\d/,
  ' ',
  /\d/,
  /\d/,
  /\d/,
  /\d/,
  ' ',
  /\d/,
  /\d/,
  /\d/,
  /\d/,
  ' ',
  /\d/,
  /\d/,
  /\d/,
  /\d/,
]

export const DebitCardContent = ({
  handleChange,
  driver,
  businessEntityStepCompleted,
}: DebitCardContentProps) => {
  const { values, setFieldValue } = useFormikContext<any>()

  const hasPrefilledName = !!values.nameOnCard

  const cardBrand = useMemo(() => {
    return getCardBrand(values.cardNumber || '')
  }, [values.cardNumber])

  const brandIconName = CARD_BRAND_ICON_MAP[cardBrand.type]

  useEffect(() => {
    if (businessEntityStepCompleted) {
      return
    }

    if (driver && !values.nameOnCard) {
      const fullName = `${driver.firstName} ${driver.lastName}`.trim()

      setFieldValue('nameOnCard', fullName)
    }
  }, [businessEntityStepCompleted, driver, values.nameOnCard, setFieldValue])

  return (
    <Fragment>
      <InfoContainer
        className={tw`mb-4 rounded-2xl p-0 py-3 px-4`}
        useInfoIcon
        dataTestId="payments-bank-debit-card-activation-info"
      >
        Debit cards need to be activated properly prior to payment or payment will be rejected.
        Cards used for the first time on this platform for payment will result in a returned
        payment. Contact support if you need help.
      </InfoContainer>
      {!businessEntityStepCompleted && (
        <div css={tw`mb-4`}>
          <FormikTextField
            name="nameOnCard"
            placeholder="Enter name on card"
            label="Name on card"
            onChange={handleChange}
            disabled={hasPrefilledName}
            css={tw`w-full`}
            inputProps={{ 'data-testid': 'payments-bank-debit-name-input' }}
          />
          {hasPrefilledName && (
            <div
              css={tw`mt-2 p-3 flex flex-row items-center rounded-2xl`}
              style={{ backgroundColor: 'var(--mui-shades-100)' }}
            >
              <div
                css={tw`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mr-3`}
                style={{ backgroundColor: '#4A3F57' }}
              >
                <Typography css={tw`text-white text-xs font-bold`}>i</Typography>
              </div>
              <div css={tw`flex flex-col`}>
                <Typography css={tw`text-sm`} style={{ color: '#4A3F57', fontSize: '16px' }}>
                  Full name is prefilled with your verified
                </Typography>
                <Typography css={tw`text-sm`} style={{ color: '#4A3F57', fontSize: '16px' }}>
                  legal name and can't be edited.
                </Typography>
                <Typography css={tw`text-sm`} style={{ color: '#4A3F57', fontSize: '16px' }}>
                  Contact support if you need help.
                </Typography>
              </div>
            </div>
          )}
        </div>
      )}
      <div css={tw`mb-4`}>
        <FormikTextField
          name="cardNumber"
          placeholder="Enter card number"
          label="Card number"
          mask={CARD_NUMBER_MASK}
          inputMode="numeric"
          onChange={handleChange}
          css={tw`w-full`}
          inputProps={{ 'data-testid': 'payments-bank-debit-card-number-input' }}
          InputProps={{
            endAdornment: brandIconName ? (
              <InputAdornment position="end" data-testid="payments-card-brand-icon">
                <GSIcon iconName={brandIconName} width={40} height={26} />
              </InputAdornment>
            ) : undefined,
          }}
        />
      </div>
      <div css={tw`mb-4`}>
        <FormikTextField
          name="expirationDate"
          placeholder="MM-YYYY"
          label="Expiration date"
          mask={DATE_MASK}
          onChange={handleChange}
          css={tw`w-full`}
          inputProps={{ 'data-testid': 'payments-bank-debit-expiration-input' }}
        />
      </div>
      <div>
        <FormikTextField
          name="zipCode"
          placeholder="Enter zip code"
          label="Zip code"
          mask={ZIP_CODE_MASK}
          inputMode="numeric"
          onChange={handleChange}
          css={tw`w-full`}
          inputProps={{ 'data-testid': 'payments-bank-debit-zip-input' }}
        />
      </div>
    </Fragment>
  )
}
